/* eslint-disable camelcase */
import { inject, Injectable } from '@angular/core'
import { CustomAuthFacade } from '@app/core/api/auth/auth.facade'
import { CdcTokenResponse } from '@app/core/models/custom/auth.model'
import { Gigya, GigyaAccountInfoParams, GigyaAccountInfoResponse, GigyaEventHandlers, ScreenSetParams } from '@app/core/models/custom/gigya.model'
import { Store } from '@ngrx/store'
import { AuthActions, EventService, GlobalMessageService, GlobalMessageType, LoginEvent, ScriptLoader, ScriptPlacement, StateWithClientAuth, WindowRef } from '@spartacus/core'
import { BehaviorSubject, catchError, Observable, take, tap } from 'rxjs'
import { environment } from 'src/environments/environment'
import { CustomAuthService } from './auth.service'
import { GlobalLoaderService } from './global-loader.service'
import { TealiumUtagService } from './tealium/tealium.service'
import { LoginTrackerService } from './login-tracker.service'

declare const gigya: Gigya

@Injectable({ providedIn: 'root' })
export class CdcService {
  private readonly winRef = inject(WindowRef)
  private readonly authFacade = inject(CustomAuthFacade)
  private readonly customAuthService = inject(CustomAuthService)
  private readonly globalLoaderService = inject(GlobalLoaderService)
  private readonly globalMessageService = inject(GlobalMessageService)
  private readonly scriptLoader = inject(ScriptLoader)
  private readonly store = inject(Store) as Store<StateWithClientAuth>
  private readonly eventService = inject(EventService)
  private readonly tealiumUtagService = inject(TealiumUtagService)
  private readonly loginTrackerService = inject(LoginTrackerService)

  wasGigyaScriptInjected$ = new BehaviorSubject<boolean>(false)

  logout (): void {
    if (this.winRef.isBrowser() && !!gigya) {
      gigya.accounts.addEventHandlers({
        onLogout: () => {
          this.tealiumUtagService.setUserLoggedOut()
          this.tealiumUtagService.trackLogout('successful')
        }
      })
      gigya.accounts.logout()
    }
    this.customAuthService.authStateChange.next(true)
  }

  showScreenset (params: ScreenSetParams): void {
    this.loginTrackerService.startTracking()
    gigya.accounts.showScreenSet({
      lang: 'en',
      onAfterScreenLoad: (...args) => {
        if (params.onAfterScreenLoad) {
          params.onAfterScreenLoad(...args)
        }
        this.loginTrackerService.startTracking()
      },
      ...params
    })
  }

  getAccountInfo (params: GigyaAccountInfoParams): void {
    gigya.accounts.getAccountInfo(params)
  }

  addGigyaEventHandlers (params: GigyaEventHandlers): void {
    gigya.accounts.addEventHandlers(params)
  }

  getCdcToken (response: GigyaAccountInfoResponse, rememberMe?: boolean): Observable<CdcTokenResponse> {
    return this.authFacade.getCdcToken({
      UID: response.UID,
      UIDSignature: response.UIDSignature,
      userId: response.profile!.email,
      signatureTimestamp: response.signatureTimestamp,
      id_token: response.id_token!,
      rememberMe
    }).pipe(
      take(1),
      tap(data => {
        if (data?.token) {
          this.customAuthService.finishAuthentication(data)
          return
        }

        this.handleEmptyTokenResponse()
      }),
      catchError(err => {
        this.globalLoaderService.setIsLoaderVisible(false)
        throw err
      })
    )
  }

  loadGigyaScript (): void {
    this.globalLoaderService.setIsLoaderVisible(true)

    if (this.winRef?.nativeWindow !== undefined) {
      (this.winRef.nativeWindow as { [key: string]: any })['__gigyaConf'] = {
        include: 'id_token'
      }

      this.scriptLoader.embedScript({
        src: environment.CDC_API_URL,
        params: { apiKey: environment.CDC_API_KEY },
        attributes: { type: 'text/javascript' },
        placement: ScriptPlacement.HEAD,
        callback: () => {
          this.wasGigyaScriptInjected$.next(true)
          this.addCdcEventHandlers()

          if (!/register|product|my-account/i.test(this.winRef.location?.href ?? '')) {
            this.globalLoaderService.setIsLoaderVisible(false)
          }
        }
      })
    }
  }

  removeAsterisksFromInputPlaceholders (formId: string): void {
    if (this.winRef.isBrowser()) {
      const form = this.winRef.document.getElementById(formId) as HTMLFormElement | null

      if (form) {
        const formElements = Array.from(form.elements) as HTMLInputElement[]

        for (const element of formElements) {
          if (/input/i.test(element.tagName) && /text|tel|number|password/i.test(element.type)) {
            element.placeholder = element.placeholder.replace('*', '')
          }
        }
      }
    }
  }

  resetPasswordWithoutScreenSet (loginID: string): Observable<void> {
    return new Observable(observer => {
      const callback = (data: GigyaAccountInfoResponse) => {
        if (data.status === 'OK') {
          observer.next()
          observer.complete()
          return
        }

        observer.error()
      }

      if (environment.CDC_API_SAPTCHA) {
        gigya.saptcha.createToken({callback: ''}).then((response) => {
          if (response.token.length > 0) {
            gigya.accounts.resetPassword({ loginID, captchaType: 'saptcha', captchaToken: response.token, callback })
          } else {
            this.globalMessageService.add({
              key: 'httpHandlers.unknownError'
            }, GlobalMessageType.MSG_TYPE_ERROR)
          }
        })
      } else {
        gigya.accounts.resetPassword({ loginID, callback })
      }
    })
  }

  public openForgotPasswordDialog (context = '') {
    if (context === '') {
      context = 'Modal: Forgot Password'
    }

    const params = {
      screenSet: 'CCG-RegistrationLogin',
      startScreen: 'gigya-forgot-password-screen'
    }

    this.openGigyaDialog(params, context)
  }

  public openLoginDialog (context = '') {
    if (context === '') {
      context = 'Modal: Login'
    }

    const params = {
      screenSet: 'CCG-RegistrationLogin',
      startScreen: 'gigya-login-screen'
    }

    this.openGigyaDialog(params, context)
  }

  protected openGigyaDialog (params: ScreenSetParams, tealiumContext: string) {
    this.tealiumUtagService.trackModalViewEvent(tealiumContext)
    this.showScreenset(params)
  }

  private addCdcEventHandlers (): void {
    gigya.accounts.addEventHandlers({
      onLogin: () => {
        this.tealiumUtagService.setUserLoggedIn()
        this.store.dispatch(new AuthActions.Login())
        this.eventService.dispatch(new LoginEvent())
      }
    })
  }

  private handleEmptyTokenResponse (): void {
    this.globalMessageService.add({
      key: 'httpHandlers.unableToLogin'
    }, GlobalMessageType.MSG_TYPE_ERROR)

    this.globalLoaderService.setIsLoaderVisible(false)
  }
}
